<template>
    <div class="main mx-auto raf">
        <div class="w-full mx-auto raf--container">
            <div class="text-center">
                <h2 class="">{{ $t('FREE_FLOWERS_TITLE') }}</h2>
                <p class="">{{RAFSubTitle}}</p>
            </div>

            <div class="w-full max-w-xs mx-auto" v-if="monthlyInvitesRemaining">
                <first-name-field v-model="name" :label="$t('FREE_FLOWERS_FRIENDS_FIRST')"/>

                <legacy-email-address-field
                    v-model="email"
                    :validation.sync="formValidations.email"
                    :label="$t('FREE_FLOWERS_EMAIL')"/>

                <!-- Just commenting gift onte so that it can be enabled again if required. -->
                <!-- <gift-note-field v-model="giftnote" :label="$t('FREE_FLOWERS_GIFT_NOTE')"/> -->

                <p class="text-center" v-html="message"></p>
                <p class="text-center error" v-html="error"></p>

                <div class="w-full my-1 text-center mb-8">
                    <submit-button
                        @click="submitForm()" :processing="isFormProcessing">
                        {{ $t('FREE_FLOWERS_SEND') }}
                    </submit-button>
                </div>
            </div>
            <img v-if="monthlyInvitesRemaining" class="raf--offer-image" src="@/assets/images/pages/free-flowers/raf-offer.png" alt="Refer a friend offer image">
        </div>
        <paper-section>
            <div class="text-center px-2 py-10">
                <h2 v-text="$t('FREE_FLOWERS_HOW_DOES')"></h2>
                <p v-text="$t('FREE_FLOWERS_EACH_MONTH')"></p>
                <p v-text="$t('FREE_FLOWERS_JUST_SEND')"></p>
                <p v-text="$t('FREE_FLOWERS_WE_ALSO')"></p>
                <h2 class="pt-6" v-text="$t('FREE_FLOWERS_YOUR_FRIEND')"></h2>
                <p class="text-center" v-text="$t('FREE_FLOWERS_IF_YOU')"></p>
                <h3 class="text-center" v-html="friendCode"></h3>

                <div class="social-icons" v-if="isMobile">
                    <h2>{{$t('FREE_FLOWERS_SOCIAL')}}</h2>
                    <div class="social-icon" @click="sendGA('whatsapp')">
                        <a :href="whatsappUrl + friendCode">
                            <font-awesome-icon class="spinner" :icon="['fab', 'whatsapp']"/>
                        </a>
                    </div>
                    <div class="social-icon" @click="sendGA('messenger')">
                        <a :href="messengerUrl + friendCode">
                            <font-awesome-icon class="spinner" :icon="['fab', 'facebook-messenger']"/>
                        </a>
                    </div>
                    <div class="social-icon" @click="sendGA('sms')">
                        <a :href="smsUrl + friendCode">
                            <font-awesome-icon class="spinner" icon="sms"/>
                        </a>
                    </div>
                </div>
            </div>
        </paper-section>
        <h2 v-if="hasExistingReferrals" class="text-center p-4" v-text="$t('FREE_FLOWERS_YOUR_INVITES')"></h2>
        <paper-section v-if="hasExistingReferrals">
            <div class="container mx-auto p-8">
                <div class="flex flex-wrap">
                    <div v-for="(referral, index) in referrals" :key="index" class="lg:w-1/3 md:w-1/2 w-full">
                        <card-invite
                            :key="index"
                            @invite-updated="getReferrals"
                            :referral="referral"/>
                    </div>
                </div>
            </div>
        </paper-section>
        <div class="text-center p-4 pb-8">
            <h2 v-text="$t('FREE_FLOWERS_CONFUSED')"></h2>
            <p>
                {{ $t('FREE_FLOWERS_VIEW') }}
                <a class="link" :href="countryUtils.zendesk_hc_referring_friends_url">
                    {{ $t('FREE_FLOWERS_FAQ') }}
                </a>
            </p>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import required from 'vuelidate/src/validators/required';
import { gaEngagement, CSPageEvents } from '@/helpers/GAHelpers';
import ReferralService from '@/services/users/ReferralService';
import PaperSection from '@/components/structure/PaperSection';
import SubmitButton from '@/components/forms/buttons/SubmitButton';
import FirstNameField from '@/components/forms/fields/FirstNameField';
import LegacyEmailAddressField from '@/components/forms/fields/LegacyEmailAddressField';
// import GiftNoteField from '@/components/forms/fields/GiftNoteField'; // for now this is disabled and may be used in future
import CardInvite from '@/components/partials/account/free-flowers/CardInvite';
import countryUtils from '@/resources/countryUtils.json';

let referralService = new ReferralService();

export default {
    name: 'FreeFlowers',
    data() {
        return {
            countryUtils: countryUtils.filter(item => item.locale.toLowerCase() === process.env.VUE_APP_DATE_LOCALE.toLowerCase())[0],
            formValidations: {
                email: false,
            },
            isFormProcessing: false,
            referrals: [],
            message: '',
            error: '',
            email: null,
            name: null,
            whatsappUrl: 'whatsapp://send?text=www.freddiesflowers.com/c/',
            messengerUrl: 'fb-messenger://share?link=www.freddiesflowers.com/c/',
            smsUrl: `sms:;?&body=${this.$t('FREE_FLOWERS_SOCIAL_SHARE_TEXT')}www.freddiesflowers.com/c/`,
            window: {
                width: 0,
                mobileBreakpoint: 768,
            },
            giftnote: null,
            hasExistingReferrals: false,
        };
    },
    computed: {
        ...mapGetters({
            numberOfBoxes: 'account/getUserReferralsRemaining',
            numberOfInvitesRemaining: 'account/getUserInvitesRemaining',
            friendCode: 'account/getUserFriendCode',
            allowsAnalytics: 'cookies/getAllowsAnalytics',
            canRefer: 'account/getUserCanRefer',
        }),

        areAllFieldsValid() {
            return !this.$v.$invalid;
        },

        boxesRemaining() {
            return this.numberOfBoxes > 0;
        },

        monthlyInvitesRemaining() {
            return this.numberOfInvitesRemaining;
        },

        isMobile() {
            return this.window.width < this.window.mobileBreakpoint;
        },

        RAFSubTitle() {
            let boxText = this.monthlyInvitesRemaining === 1 ? 'box' : 'boxes';
            return this.monthlyInvitesRemaining ? this.$t('FREE_FLOWERS_SUB_TITLE', { boxText, boxNumber: this.monthlyInvitesRemaining }) : this.$t('FREE_FLOWERS_NO_BOXES');
        },
    },
    created() {
        if (!this.canRefer) this.$router.push({ name: 'home' });
    },
    mounted() {
        this.getReferrals();

        window.addEventListener('resize', this.handleResize);
        this.handleResize();
    },
    methods: {
        handleResize() {
            this.window.width = window.innerWidth;
        },

        resetMessages() {
            if (this.name === null) this.name = '';
            if (this.email === null) this.email = '';
            if (this.giftnote === null) this.giftnote = '';
        },
        getReferrals() {
            referralService.getReferrals()
                .then(response => {
                    this.referrals = response.data.items;
                }).finally(() => {
                    let referralsCount = Object.keys(this.referrals);
                    if (referralsCount.length) {
                        this.hasExistingReferrals = true;
                    }
                })
                .catch(() => {
                    this.error = this.$t('FREE_FLOWERS_SOMETHING_WENT_WRONG');
                });
        },
        postReferral() {
            referralService.postReferral(this.name, this.email, this.giftnote)
                .then(() => {
                    this.message = this.$t('FREE_FLOWERS_THANKS', {
                        'name': this.$t(`${this.name}`),
                    });

                    this.name = null;
                    this.email = null;
                    this.giftnote = null;

                    this.getReferrals();
                    this.$store.dispatch('account/fetchUserData', true);
                })
                .catch(() => {
                    this.error = this.$t('FREE_FLOWERS_SOMETHING_WENT_WRONG');
                });
        },
        submitForm() {
            this.toggleFormProcessing();

            if (this.allowsAnalytics) {
                setTimeout(() => {
                    this.validateForm();
                    gaEngagement('click', 'Send invite');
                    CSPageEvents('invite-sent');
                }, 1000);
            }
        },
        sendGA(channel) {
            gaEngagement('click', `Send invite via ${channel}`);
        },
        toggleFormProcessing() {
            this.isFormProcessing = !this.isFormProcessing;
        },
        validateForm() {
            this.$v.$touch();

            this.areAllFieldsValid ? this.postReferral() : this.resetMessages();

            this.toggleFormProcessing();
        },
    },
    validations: {
        email: {
            isValid() {
                return this.formValidations.email;
            },
        },
        name: {
            required,
        },
    },
    components: {
        LegacyEmailAddressField,
        FirstNameField,
        // GiftNoteField,
        PaperSection,
        CardInvite,
        SubmitButton,
    },
};
</script>

<style scoped lang="scss">
    .social-icons {
        text-align: center;
        margin-top: 30px;

        .social-icon {
            display: inline-flex;
            gap: 20px;
            width: 64px;
            height: 64px;
            margin: 0 auto;

            a {
                margin: 0 auto;
                font-size: 40px;
            }
        }
    }
    .raf {
        &--container {
            width: 70%;
            margin: 0px auto;
            max-width: 800px;
            position: relative;
        }
        &--offer-image {
            position: absolute;
            top: 30%;
            right: -100px;
            @media (max-width: 768px) {
                position: relative;
                top: auto;
                right: auto;
                margin: 10px auto;
            }
        }
    }
</style>
